/**
 * Host for a Shopify <video> or YouTube/Vimeo iframe with a glass play/pause button.
 * Markup: snippets/bt-video-toggle.liquid (`[data-bt-video-toggle]`).
 *
 * `data-autoplay="true"` keeps native muted autoplay + preload, plays while the
 * host is on screen, pauses when it leaves (desktop/mobile gallery duplicates),
 * and respects reduced motion.
 */
class BtVideoToggle extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #userPaused = false;

  #reducedMotion = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const button = this.querySelector('[data-bt-video-toggle]');
    if (!(button instanceof HTMLButtonElement)) return;
    this.#button = button;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    button.addEventListener('click', this.#onToggle, { signal });
    this.#stripNativeControls();

    for (const video of this.#videos()) {
      if (this.#autoplayEnabled) video.muted = true;
      video.addEventListener('play', this.#onNativePlay, { signal });
      video.addEventListener('pause', this.#onNativePause, { signal });
      video.addEventListener('ended', this.#onNativeEnded, { signal });
    }

    if (this.#reducedMotion) {
      this.pause();
    }

    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.4 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abort?.abort();
    this.#abort = null;
    this.#button = null;
  }

  pause() {
    this.#pauseMedia();
    this.#syncButton(false);
  }

  get #autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !this.#reducedMotion;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    const entry = entries[0];
    if (!entry) return;

    if (entry.isIntersecting) {
      if (this.#autoplayEnabled && !this.#userPaused) {
        this.#playMedia();
        this.#syncButton(true);
      }
      return;
    }

    this.#pauseMedia();
    if (!this.#autoplayEnabled || this.#userPaused) this.#syncButton(false);
  };

  /** @param {MouseEvent} event */
  #onToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#button?.dataset.playing === 'true') {
      this.#userPaused = true;
      this.pause();
      return;
    }

    this.#userPaused = false;
    if (!this.#autoplayEnabled) this.#pauseSiblings();
    this.#playMedia();
    this.#syncButton(true);
  };

  #onNativePlay = () => {
    this.#syncButton(true);
  };

  #onNativePause = () => {
    const video = this.#videos()[0];
    if (video && !video.paused && !video.ended) return;
    if (this.#autoplayEnabled && !this.#userPaused) return;
    this.#syncButton(false);
  };

  #onNativeEnded = () => {
    this.#userPaused = true;
    this.#syncButton(false);
  };

  #stripNativeControls() {
    for (const video of this.#videos()) {
      video.controls = false;
      video.removeAttribute('controls');
      if (this.#autoplayEnabled) {
        video.muted = true;
        video.autoplay = true;
        video.setAttribute('autoplay', '');
        video.preload = 'auto';
      } else {
        video.removeAttribute('autoplay');
        video.autoplay = false;
      }
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
    }

    for (const iframe of this.querySelectorAll('iframe')) {
      this.#ensureFrameParams(iframe);
    }
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #ensureFrameParams(iframe) {
    const src = iframe.getAttribute('src');
    if (!src) return;

    try {
      const url = new URL(src, window.location.href);
      const isYoutube = url.hostname.includes('youtube');
      const isVimeo = url.hostname.includes('vimeo');
      if (!isYoutube && !isVimeo) return;

      let changed = false;
      const set = (key, value) => {
        if (url.searchParams.get(key) !== value) {
          url.searchParams.set(key, value);
          changed = true;
        }
      };

      set('controls', '0');
      if (isYoutube) set('enablejsapi', '1');
      if (this.#autoplayEnabled) {
        set(isYoutube ? 'mute' : 'muted', '1');
        set('autoplay', '1');
      }

      if (changed) iframe.src = url.toString();
    } catch {
      // Ignore malformed embed URLs.
    }
  }

  #pauseSiblings() {
    const root = this.closest('media-gallery') || this.parentElement;
    if (!root) return;
    for (const host of root.querySelectorAll('bt-video-toggle')) {
      if (host !== this && host instanceof BtVideoToggle) {
        host.#userPaused = true;
        host.pause();
      }
    }
  }

  #playMedia() {
    for (const video of this.#videos()) {
      if (this.#autoplayEnabled) video.muted = true;
      video.play().catch(() => {});
    }
    this.#postToFrames('play');
  }

  #pauseMedia() {
    for (const video of this.#videos()) {
      video.pause();
    }
    this.#postToFrames('pause');
  }

  /**
   * @param {'play' | 'pause'} action
   */
  #postToFrames(action) {
    for (const iframe of this.querySelectorAll('iframe')) {
      if (!iframe.contentWindow) continue;

      const type = iframe.dataset.videoType || '';
      const isYoutube = type === 'youtube' || iframe.classList.contains('js-youtube');
      const isVimeo = type === 'vimeo' || iframe.classList.contains('js-vimeo');

      if (isYoutube) {
        const func = action === 'play' ? 'playVideo' : 'pauseVideo';
        iframe.contentWindow.postMessage(`{"event":"command","func":"${func}","args":""}`, '*');
      } else if (isVimeo) {
        iframe.contentWindow.postMessage(`{"method":"${action}"}`, '*');
      }
    }
  }

  /**
   * @param {boolean} playing
   */
  #syncButton(playing) {
    const button = this.#button;
    if (!button) return;

    button.dataset.playing = playing ? 'true' : 'false';
    button.setAttribute(
      'aria-label',
      playing ? button.dataset.pauseLabel || 'Pause video' : button.dataset.playLabel || 'Play video'
    );
    button.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !playing);
    button.querySelector('[data-icon-play]')?.toggleAttribute('hidden', playing);
  }

  /** @returns {HTMLVideoElement[]} */
  #videos() {
    return Array.from(this.querySelectorAll('video'));
  }
}

if (!customElements.get('bt-video-toggle')) {
  customElements.define('bt-video-toggle', BtVideoToggle);
}

export { BtVideoToggle };
